import { buysSnack } from '../../domain/happiness.js';
import { PHASE } from '../../domain/operations.js';
import { randomInteger } from './random.js';
import type { FlowFacts, FlowPassenger, FlowPlane, FlowState, TransitPassenger } from './types.js';

/** Business cash posts in whole bills from a remainder of 2000 subunits; anything above the money cap is published as blocked. */
function postFlowCash(state: FlowState, account: { pending: number; cashRemainder: number }, payout: number, half: boolean): void {
    const credit = account.cashRemainder + payout * (half ? 1000 : 2000);
    const whole = Math.floor(credit / 2000), posted = Math.min(whole, state.moneyLimit - account.pending);
    account.cashRemainder = credit % 2000; account.pending += posted;
    state.businessRevenue += posted; state.blockedRevenue += whole - posted;
}

export function advanceFlowService(state: FlowState, facts: FlowFacts, half = false): void {
    for (const [n, department] of state.departments.entries()) {
        const spec = facts.departments[n], downstream = state.departments[n + 1];
        for (const passenger of [...department.queued]) {
            passenger.patienceFrames = Math.max(0, passenger.patienceFrames - 60);
            passenger.leavingFrames = 0;
            if (passenger.patienceFrames === 0) passenger.happiness = Math.max(10, (passenger.happiness ?? 80) - 3);

        }
        const bags = () => state.freeBagMilli + state.porters.reduce((sum, p) => sum + p.carriedMilli, 0);
        const reserved = (p: FlowPlane) => p.boarded + state.reservations.filter(r => r.gate === p.gate).length;
        // Release one served passenger downstream; false keeps them holding their slot unpaid.
        const release = (passenger: FlowPassenger): boolean => {
            if (spec.index === 1 && bags() >= 24000) return false;
            if (downstream) {
                const occupancy = downstream.queued.length + downstream.inService.length + downstream.completed.length
                    + state.transit.filter(p => p.destination === downstream.index).length + (downstream.index === 6 ? state.lounge.length + state.inquiry.length : 0);
                if (occupancy >= 12) return false;
            }
            let plane: FlowPlane | undefined;
            if (!downstream) {
                plane = state.planes.filter(p => p.phase === PHASE.BOARDING && p.boardingCalled && reserved(p) < p.seats
                    && (p.boarded === 0 || state.frame < Math.max(p.scheduledDepartureFrame, p.boardingStartedFrame) + facts.tier.boardingWindow))
                    .sort((a, b) => reserved(a) / a.seats - reserved(b) / b.seats || a.gate - b.gate)[0];
                if (!plane) return false;
            }
            postFlowCash(state, department, spec.payout * (passenger.vip ? 2 : 1), half);
            passenger.happiness = Math.min(100, (passenger.happiness ?? 80) + 5);
            department.servedMilli += passenger.massMilli;
            department.completed.splice(department.completed.indexOf(passenger), 1);
            if (spec.index === 1) { state.freeBagMilli += passenger.massMilli; state.producedBagMilli += passenger.massMilli; }
            if (spec.index === 0 && buysSnack(passenger.serial, passenger.happiness ?? 80)) {
                const selected = facts.tier.vending[randomInteger(state, 0, facts.tier.vending.length - 1)], machine = state.vending[selected];
                postFlowCash(state, machine, facts.vending[machine.index].payout, half); machine.soldMilli += passenger.massMilli;
            }
            if (downstream) {
                const moving = passenger as TransitPassenger;
                moving.patienceFrames = facts.queuePatience; moving.leavingFrames = 0;
                moving.destination = facts.departments[n + 1].index; moving.remainingFrames = spec.transferFrames; state.transit.push(moving);
            } else state.reservations.push({ serial: passenger.serial, vip: passenger.vip, massMilli: passenger.massMilli, gate: plane!.gate, remainingFrames: facts.boardingFrames[plane!.gate] });
            return true;
        };
        // Authored approach delay occupies its slot; completed passengers retain that slot until transfer.
        const seat = (slot: number): FlowPassenger | null => {
            const passenger = department.queued.shift();
            if (!passenger) return null;
            passenger.remainingWork = spec.serviceFrames * 4000; passenger.slot = slot; passenger.approachFrames = spec.approachFrames[slot];
            department.inService.push(passenger); return passenger;
        };
        const fill = () => { const used = new Set([...department.inService, ...department.completed].map(p => p.slot)); for (let slot = 0; slot < spec.capacity; slot++) if (!used.has(slot)) seat(slot); };
        for (const passenger of [...department.completed]) if (!release(passenger)) break;
        fill();
        // A passenger question occupies the boarding desk; a full rack stops Bag Drop.
        if (spec.workers && !(spec.index === 1 && bags() >= 24000) && !(spec.index === 6 && state.inquiry.length)) {
            // Each slot spends exactly one second of frames; work left from a finished passenger carries to the next.
            const cleanliness = Math.max(750, 1000 - department.litter * 25), rate = (100 + Math.min(2, spec.workers - 1) * 55) * cleanliness * 40 / 1000;
            for (let slot = 0; slot < spec.capacity; slot++) {
                let budget = 60, passenger: FlowPassenger | null | undefined = department.inService.find(p => p.slot === slot);
                while (passenger && budget > 0) {
                    const walk = Math.min(passenger.approachFrames!, budget); passenger.approachFrames! -= walk; budget -= walk;
                    if (passenger.approachFrames! > 0) break;
                    const frames = Math.min(budget, Math.ceil(passenger.remainingWork! / rate));
                    passenger.remainingWork = Math.max(0, passenger.remainingWork! - frames * rate); budget -= frames;
                    if (passenger.remainingWork > 0) break;
                    department.inService.splice(department.inService.indexOf(passenger), 1); department.completed.push(passenger);
                    if (department.completed.length > 1 || !release(passenger)) break;
                    passenger = budget > 0 ? seat(slot) : null;
                }
            }
        }
        fill();
    }
}
